import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
// eslint-disable-next-line no-unused-vars
import Chart from 'chart.js/auto';
import Loader from '../../../Components/utils/Loader';
import { CSVLink } from 'react-csv';
import { FiDownload } from 'react-icons/fi';
import { exportToPDF } from '../../../utils/exportUtils';

const API = process.env.REACT_APP_Backend_Host_Name;
const COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ec4899','#8b5cf6','#ef4444','#14b8a6','#f97316','#64748b','#a855f7','#06b6d4','#84cc16','#e11d48','#0891b2'];

function AnalyticsListings({ query }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('orders'); // 'orders' or 'views'
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/api/v1/settings/admin/analytics/listings?${query}`, { headers: { Authorization: `Admin ${token}` } })
            .then(res => setData(res.data))
            .finally(() => setLoading(false));
    }, [query, token]);

    if (loading) return <Loader type="simpleMax" />;
    if (!data) return null;

    const chartOpts = { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } } }, scales: { x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 12 } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 } }, beginAtZero: true } } };
    const doughnutOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } } }, cutout: '65%' };

    const listingsChart = {
        labels: data.b2cListingsOverTime.map(d => d.date),
        datasets: [
            { label: 'B2C Listings', data: data.b2cListingsOverTime.map(d => d.count), borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
            { label: 'B2B Listings', data: data.b2bListingsOverTime.map(d => d.count), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
        ]
    };

    const typeChart = {
        labels: ['Products', 'Services'],
        datasets: [{ data: [data.productVsService.products, data.productVsService.services], backgroundColor: ['#6366f1', '#f59e0b'], borderWidth: 0 }]
    };

    const pricingChart = {
        labels: ['Fixed', 'Variable'],
        datasets: [{ data: [data.pricingDistribution.fixed, data.pricingDistribution.variable], backgroundColor: ['#0ea5e9', '#ec4899'], borderWidth: 0 }]
    };

    const catChart = {
        labels: data.categoryPerformance.map(c => c._id?.length > 18 ? c._id.slice(0, 18) + '…' : c._id),
        datasets: [{ label: 'Listings', data: data.categoryPerformance.map(c => c.count), backgroundColor: COLORS.slice(0, data.categoryPerformance.length), borderRadius: 4, barThickness: 18 }]
    };

    const topListings = viewMode === 'orders' ? data.topByOrders : data.topByViews;
    
    const csvData = data ? [
        { Metric: 'B2C Listings', Value: data.productVsService.products + data.productVsService.services, Extra: '', More: '' },
        { Metric: 'B2B Listings', Value: data.b2bStatusDistribution.reduce((s, d) => s + d.count, 0), Extra: '', More: '' },
        { Metric: 'On Sale', Value: data.onSaleCount, Extra: '', More: '' },
        { Metric: 'Categories', Value: data.categoryPerformance.length, Extra: '', More: '' },
        { Metric: '', Value: '', Extra: '', More: '' },
        { Metric: 'LISTING TYPES', Value: '', Extra: '', More: '' },
        { Metric: 'Products', Value: data.productVsService.products, Extra: '', More: '' },
        { Metric: 'Services', Value: data.productVsService.services, Extra: '', More: '' },
        { Metric: '', Value: '', Extra: '', More: '' },
        { Metric: 'PRICING TYPES', Value: '', Extra: '', More: '' },
        { Metric: 'Fixed', Value: data.pricingDistribution.fixed, Extra: '', More: '' },
        { Metric: 'Variable', Value: data.pricingDistribution.variable, Extra: '', More: '' },
        { Metric: '', Value: '', Extra: '', More: '' },
        { Metric: 'CATEGORY PERFORMANCE', Value: '', Extra: '', More: '' },
        { Metric: 'Category', Value: 'Listings', Extra: 'Impressions', More: 'Orders' },
        ...data.categoryPerformance.map(c => ({ Metric: c._id, Value: c.count, Extra: c.totalImpressions, More: c.totalOrders })),
        { Metric: '', Value: '', Extra: '', More: '' },
        { Metric: 'TOP LISTINGS (By Orders)', Value: '', Extra: '', More: '' },
        { Metric: 'Title', Value: 'Category', Extra: 'Orders', More: 'Impressions' },
        ...data.topByOrders.map(p => ({ Metric: p.title, Value: p.category, Extra: p.orders, More: p.impressions })),
        { Metric: '', Value: '', Extra: '', More: '' },
        { Metric: 'LISTINGS OVER TIME', Value: '', Extra: '', More: '' },
        { Metric: 'Date', Value: 'B2C Listings', Extra: 'B2B Listings', More: '' },
        ...data.b2cListingsOverTime.map((d, i) => ({
            Metric: d.date,
            Value: d.count,
            Extra: data.b2bListingsOverTime[i]?.count || 0,
            More: ''
        }))
    ] : [];

    return (
        <div className="analyticsListingsTab" id="analytics-listings-report">
            <div className="analyticsExportBar">
                <CSVLink data={csvData} filename="listings_report.csv" className="exportBtn csvBtn">
                    <FiDownload /> Export CSV
                </CSVLink>
                <button onClick={() => exportToPDF('analytics-listings-report', 'listings_report')} className="exportBtn pdfBtn">
                    <FiDownload /> Export PDF
                </button>
            </div>

            <div className="analyticsSimpleStats fourCol">
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#6366f1' }}>{data.productVsService.products + data.productVsService.services}</div><div className="simpleStatLabel">B2C Listings</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#10b981' }}>{data.b2bStatusDistribution.reduce((s, d) => s + d.count, 0)}</div><div className="simpleStatLabel">B2B Listings</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#f59e0b' }}>{data.onSaleCount}</div><div className="simpleStatLabel">On Sale</div></div>
                <div className="simpleStat"><div className="simpleStatValue" style={{ color: '#ec4899' }}>{data.categoryPerformance.length}</div><div className="simpleStatLabel">Categories</div></div>
            </div>

            <div className="analyticsChartCard">
                <h3 className="chartCardTitle">New Listings Over Time</h3>
                <div className="chartContainer" style={{ height: 280 }}><Line data={listingsChart} options={chartOpts} /></div>
            </div>

            <div className="analyticsGridRow three">
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Products vs Services</h3>
                    <div className="chartContainer" style={{ height: 220 }}><Doughnut data={typeChart} options={doughnutOpts} /></div>
                </div>
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Pricing Type</h3>
                    <div className="chartContainer" style={{ height: 220 }}><Doughnut data={pricingChart} options={doughnutOpts} /></div>
                </div>
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Listings by Category</h3>
                    <div className="chartContainer" style={{ height: 220 }}><Bar data={catChart} options={{ ...chartOpts, indexAxis: 'y', plugins: { ...chartOpts.plugins, legend: { display: false } } }} /></div>
                </div>
            </div>

            <div className="analyticsChartCard">
                <h3 className="chartCardTitle">Category Performance</h3>
                <div className="analyticsTableWrap">
                    <table className="analyticsTable">
                        <thead><tr><th>Category</th><th>Listings</th><th>Impressions</th><th>Clicks</th><th>Orders</th><th>Avg Rating</th></tr></thead>
                        <tbody>
                            {data.categoryPerformance.map((c, i) => (
                                <tr key={i}><td>{c._id}</td><td>{c.count}</td><td>{c.totalImpressions.toLocaleString()}</td><td>{c.totalClicks.toLocaleString()}</td><td>{c.totalOrders}</td><td>⭐ {c.avgRating?.toFixed(1) || '0'}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="analyticsChartCard">
                <div className="chartCardHeaderRow">
                    <h3 className="chartCardTitle">Top Performing Listings</h3>
                    <div className="miniToggle">
                        <button className={viewMode === 'orders' ? 'active' : ''} onClick={() => setViewMode('orders')}>By Orders</button>
                        <button className={viewMode === 'views' ? 'active' : ''} onClick={() => setViewMode('views')}>By Views</button>
                    </div>
                </div>
                <div className="analyticsTableWrap">
                    <table className="analyticsTable">
                        <thead><tr><th>#</th><th>Listing</th><th>Category</th><th>Type</th><th>Orders</th><th>Impressions</th><th>Rating</th></tr></thead>
                        <tbody>
                            {topListings.map((p, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td className="sellerCell">{p.thumbnail && <img src={p.thumbnail} alt="" className="sellerThumb" />}<span className="listingTitle">{p.title}</span></td>
                                    <td>{p.category}</td>
                                    <td><span className={`typeBadgeMini ${p.listingType}`}>{p.listingType}</span></td>
                                    <td>{p.orders}</td>
                                    <td>{p.impressions?.toLocaleString()}</td>
                                    <td>⭐ {p.rating?.toFixed(1) || '0'} ({p.reviews})</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AnalyticsListings;
